import React, { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { css } from '@linaria/core';
import { styled } from '@linaria/react';
import { Checkbox, Dialog, Heading, Stack, breakpoints, zIndex } from '@spaceshipapp/cambridge';
import Button from '../../../components/Button';
import * as LocalStorage from '../../../utils/localStorage';
import * as analytics from '../../../analytics/auth';

export const LOCAL_STORAGE_KEY = 'dismissTNC';

interface Props {
  onClose(): void;
}

interface TermsItemProps {
  children?: string;
}

const TermsAndConditions: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation('common');
  const [dismiss, setDismiss] = useState(true);

  const handleConfirm = () => {
    if (dismiss) {
      LocalStorage.saveItem(LOCAL_STORAGE_KEY, 'true');
    }

    analytics.sendTermsAndConditionsAccepted();
    onClose();
  };

  const handleDismiss = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setDismiss(checked);
  };

  return (
    <Dialog title={t('TermsAndConditions.title')} className={modalStyle}>
      <Container>
        <Content>
          <Stack gap="md">
            <Heading as="h4">{t('Terms.heading')}</Heading>
            <p>{t('Terms.introduction', {}, { returnObjects: true })}</p>
            <UL>
              <li>{t('Terms.content', {}, { returnObjects: true })[0]}</li>
              <li>
                {t('Terms.content', {}, { returnObjects: true })[1]}
                <Sublist>
                  {t<TermsItemProps[]>('Terms.items', {}, { returnObjects: true }).map(
                    (item: TermsItemProps, index: number) => {
                      return <li key={index}>{item}</li>;
                    },
                  )}
                </Sublist>
              </li>
            </UL>
            <p>{t('Terms.terms', {}, { returnObjects: true })}</p>
            <Heading as="h4">{t('Terms.subHeading')}</Heading>
            <p>{t('Terms.agreement', {}, { returnObjects: true })}</p>
          </Stack>
        </Content>
        <Checkbox onChange={handleDismiss} checked={dismiss}>
          {t('TermsAndConditions.dontShowAgain')}
        </Checkbox>
        <Button
          variant="primary"
          onClick={handleConfirm}
          data-tracking-label={t('commonEn:TermsAndConditions.buttonAgree')}
        >
          {t('TermsAndConditions.buttonAgree')}
        </Button>
      </Container>
    </Dialog>
  );
};

const modalStyle = css`
  position: fixed;
  bottom: 0;
  z-index: ${zIndex.MODAL + 1};

  @media (min-width: ${breakpoints.md}) {
    top: 50%;
    left: 50%;
    max-width: 28rem;
    bottom: auto;
    transform: translate(-50%, -50%);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 60vh;

  > *:not(:first-child) {
    margin-top: 1rem;
  }
`;

const Content = styled.div`
  flex-grow: 1;
  overflow-y: scroll;
`;

const UL = styled.ul`
  list-style-type: disc;
  list-style-position: inside;
`;

const Sublist = styled.ul`
  list-style-type: circle;
  list-style-position: inside;
  margin-left: 1rem;
`;

export default TermsAndConditions;
